'use client';

import { useState } from 'react';

interface CaseHighlight {
  title: string;
  outcome: string;
}

interface Attorney {
  image: string;
  alt: string;
  role: string;
  name: string;
  focus: string;
  credential: string;
  bio: string;
  achievements: string[];
  caseHighlights: CaseHighlight[];
  barAdmissions: string[];
}

const attorneys: Attorney[] = [
  {
    image: "/assets/images/richard_harrington_attorney.png",
    alt: 'Richard Harrington, Founding Partner at Harrington & Associates LLP, professional headshot',
    role: 'Founding Partner',
    name: 'Richard Harrington',
    focus: 'Business Law & Litigation',
    credential: 'Columbia Law School, J.D. 2000',
    bio: 'Richard Harrington founded Harrington & Associates LLP after more than two decades of high-stakes commercial litigation in New York City. A Columbia Law graduate, Richard built his reputation defending Fortune 500 companies and emerging enterprises alike against complex contract disputes, shareholder actions, and regulatory investigations. His courtroom presence and strategic acumen have made him one of the most sought-after business litigators in the tri-state area.',
    achievements: [
      'Named to New York Super Lawyers® for 10 consecutive years',
      'Recognized by Chambers USA as a leading business litigator',
      'Former Chair, NYSBA Commercial & Federal Litigation Section',
      'Secured over $500M in verdicts and settlements for business clients',
      'Adjunct Professor of Commercial Litigation, Columbia Law School',
    ],
    caseHighlights: [
      { title: 'Multinational Contract Dispute', outcome: 'Obtained $47M verdict for a technology firm in a breach-of-contract action against a European supplier.' },
      { title: 'Shareholder Derivative Action', outcome: 'Successfully defended a NYSE-listed company, achieving full dismissal at the pleadings stage.' },
      { title: 'SEC Regulatory Defense', outcome: 'Guided a financial services client through an SEC investigation resulting in no charges filed.' },
    ],
    barAdmissions: ['New York', 'U.S. District Court, S.D.N.Y.', 'U.S. District Court, E.D.N.Y.', 'U.S. Court of Appeals, 2nd Circuit'],
  },
  {
    image: "/assets/images/marcus_webb_attorney.png",
    alt: 'Marcus Webb, Senior Partner at Harrington & Associates LLP, professional headshot',
    role: 'Senior Partner',
    name: 'Marcus Webb',
    focus: 'Personal Injury & Civil Litigation',
    credential: 'NYU School of Law, J.D. 2008',
    bio: 'Marcus Webb is a tenacious trial attorney whose practice centers on representing individuals and families who have suffered serious injuries due to negligence. Since joining the firm, Marcus has recovered hundreds of millions of dollars for clients injured in construction accidents, motor vehicle collisions, and medical malpractice incidents across New York City and the surrounding region. His client-first philosophy and relentless preparation set him apart in the courtroom.',
    achievements: [
      'Multi-Million Dollar Advocates Forum member',
      'New York Rising Stars® — Personal Injury, 2015–2023',
      'Recovered over $300M in personal injury verdicts and settlements',
      'Selected to the National Trial Lawyers: Top 40 Under 40',
      'Peer-rated AV Preeminent® by Martindale-Hubbell',
    ],
    caseHighlights: [
      { title: 'Construction Site Fall — Manhattan', outcome: 'Secured $18.5M settlement for a worker who sustained catastrophic spinal injuries on a midtown high-rise project.' },
      { title: 'Medical Malpractice — Surgical Error', outcome: 'Won $9.2M jury verdict for a patient who suffered permanent disability following a negligent surgical procedure.' },
      { title: 'Multi-Vehicle Highway Collision', outcome: 'Recovered $4.8M for a family of four injured by a commercial truck driver operating in violation of federal hours-of-service regulations.' },
    ],
    barAdmissions: ['New York', 'New Jersey', 'U.S. District Court, S.D.N.Y.', 'U.S. District Court, E.D.N.Y.'],
  },
  {
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_12672b149-1763294392419.png",
    alt: 'Priya Nair, Partner at Harrington & Associates LLP, professional headshot',
    role: 'Partner',
    name: 'Priya Nair',
    focus: 'Employment Law & HR Compliance',
    credential: 'DePaul University College of Law, J.D. 2016',
    bio: 'Priya Nair brings a dual perspective to employment law — having counseled both employers and employees through the full spectrum of workplace disputes. Her practice encompasses discrimination and harassment claims, wage-and-hour class actions, executive severance negotiations, and proactive HR compliance audits. Priya is known for translating complex legal obligations into practical workplace policies that protect clients before disputes arise.',
    achievements: [
      'Named a "Top Employment Lawyer" by New York Law Journal, 2021 & 2022',
      'Certified Specialist in Labor & Employment Law, NYSBA',
      'Successfully defended 50+ employers in EEOC proceedings',
      'Speaker, New York City Bar Association Employment Law Institute',
      'Co-author, "Navigating NYC\'s Expanded Workplace Protections" (2023)',
    ],
    caseHighlights: [
      { title: 'Wage-and-Hour Class Action Defense', outcome: 'Defeated class certification in a 1,200-employee FLSA collective action, saving client an estimated $22M in exposure.' },
      { title: 'Executive Wrongful Termination', outcome: 'Negotiated a $3.1M severance package for a C-suite executive following discriminatory termination.' },
      { title: 'Hostile Work Environment Claim', outcome: 'Secured summary judgment dismissal of a high-profile harassment claim against a major New York media company.' },
    ],
    barAdmissions: ['New York', 'Connecticut', 'U.S. District Court, S.D.N.Y.'],
  },
  {
    image: "/assets/images/james_mitchell_attorney.png",
    alt: 'James Mitchell, Associate at Harrington & Associates LLP, professional headshot',
    role: 'Associate',
    name: 'James Mitchell',
    focus: 'Family Law & Mediation',
    credential: 'Fordham University School of Law, J.D. 2010',
    bio: 'James Mitchell approaches family law with both legal precision and genuine empathy. He guides clients through some of the most emotionally charged chapters of their lives — divorce, child custody, asset division, and post-judgment modifications — with a focus on achieving durable, fair resolutions. As a trained mediator, James frequently helps families reach agreements outside the courtroom, reducing cost, conflict, and time.',
    achievements: [
      'Certified Family Law Mediator, New York State',
      'New York Rising Stars® — Family Law, 2018–2023',
      'Recognized by Best Lawyers in America®: Ones to Watch, 2022',
      'Pro bono counsel for 100+ low-income families through NYC Family Court',
      'Panelist, Fordham Family Law Symposium, 2021 & 2023',
    ],
    caseHighlights: [
      { title: 'High-Net-Worth Divorce — Manhattan', outcome: 'Negotiated equitable division of a $28M marital estate including real estate, business interests, and investment portfolios.' },
      { title: 'International Child Custody Dispute', outcome: 'Secured sole custody for a New York parent in a Hague Convention proceeding involving a child wrongfully retained abroad.' },
      { title: 'Complex Mediation — Blended Family Estate', outcome: 'Facilitated a mediated settlement resolving competing inheritance and custody claims across three households, avoiding protracted litigation.' },
    ],
    barAdmissions: ['New York', 'New Jersey', 'U.S. District Court, S.D.N.Y.'],
  },
];

export default function AttorneySection() {
  const [activeAttorney, setActiveAttorney] = useState<Attorney | null>(null);
  const [activeTab, setActiveTab] = useState<'bio' | 'achievements' | 'cases'>('bio');

  const openModal = (attorney: Attorney) => {
    setActiveAttorney(attorney);
    setActiveTab('bio');
    document.body.style.overflow = 'hidden';
  };

  const closeModal = () => {
    setActiveAttorney(null);
    document.body.style.overflow = '';
  };

  return (
    <>
      <section id="attorneys" className="attorney-section section-space">
        <div className="site-container">
          <div className="section-heading-row">
            <div>
              <p className="eyebrow">The team</p>
              <h2>Our <em>Attorneys</em></h2>
            </div>
            <a href="#contact" className="text-link">
              Schedule a consultation
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M7 7h10v10" />
                <path d="M7 17 17 7" />
              </svg>
            </a>
          </div>
          <div className="attorney-grid">
            {attorneys.map((attorney, i) => (
              <article key={i} className="attorney-card attorney-card--interactive">
                <div className="attorney-image">
                  <img src={attorney.image} alt={attorney.alt} />
                  <div className="attorney-card-overlay">
                    <button
                      className="attorney-view-btn"
                      onClick={() => openModal(attorney)}
                      aria-label={`View full profile for ${attorney.name}`}
                    >
                      View Full Profile
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M7 7h10v10" /><path d="M7 17 17 7" />
                      </svg>
                    </button>
                  </div>
                </div>
                <p className="attorney-role">{attorney.role}</p>
                <h3>{attorney.name}</h3>
                <p className="attorney-focus">{attorney.focus}</p>
                <p className="attorney-credential">{attorney.credential}</p>
                <button
                  className="attorney-bio-link"
                  onClick={() => openModal(attorney)}
                  aria-label={`Read bio for ${attorney.name}`}
                >
                  Read Bio &amp; Case Highlights
                  <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M7 7h10v10" /><path d="M7 17 17 7" />
                  </svg>
                </button>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Attorney Bio Modal */}
      {activeAttorney && (
        <div
          className="attorney-modal-backdrop"
          onClick={(e) => { if (e.target === e.currentTarget) closeModal(); }}
          role="dialog"
          aria-modal="true"
          aria-label={`${activeAttorney.name} full profile`}
        >
          <div className="attorney-modal">
            {/* Modal Header */}
            <div className="attorney-modal-header">
              <div className="attorney-modal-identity">
                <div className="attorney-modal-photo">
                  <img src={activeAttorney.image} alt={activeAttorney.alt} />
                </div>
                <div>
                  <p className="attorney-modal-role">{activeAttorney.role}</p>
                  <h2 className="attorney-modal-name">{activeAttorney.name}</h2>
                  <p className="attorney-modal-focus">{activeAttorney.focus}</p>
                  <p className="attorney-modal-credential">{activeAttorney.credential}</p>
                </div>
              </div>
              <button className="attorney-modal-close" onClick={closeModal} aria-label="Close profile">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 6 6 18" /><path d="m6 6 12 12" />
                </svg>
              </button>
            </div>

            {/* Tabs */}
            <div className="attorney-modal-tabs">
              {(['bio', 'achievements', 'cases'] as const).map((tab) => (
                <button
                  key={tab}
                  className={`attorney-modal-tab${activeTab === tab ? ' attorney-modal-tab--active' : ''}`}
                  onClick={() => setActiveTab(tab)}
                >
                  {tab === 'bio' ? 'Biography' : tab === 'achievements' ? 'Achievements' : 'Case Highlights'}
                </button>
              ))}
            </div>

            {/* Tab Content */}
            <div className="attorney-modal-body">
              {activeTab === 'bio' && (
                <div className="attorney-modal-bio">
                  <p>{activeAttorney.bio}</p>
                  <div className="attorney-modal-bar-section">
                    <h4>Bar Admissions</h4>
                    <ul className="attorney-bar-list">
                      {activeAttorney.barAdmissions.map((bar, i) => (
                        <li key={i}>{bar}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

              {activeTab === 'achievements' && (
                <ul className="attorney-achievements-list">
                  {activeAttorney.achievements.map((item, i) => (
                    <li key={i}>
                      <span className="achievement-bullet">
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      </span>
                      {item}
                    </li>
                  ))}
                </ul>
              )}

              {activeTab === 'cases' && (
                <div className="attorney-cases-list">
                  {activeAttorney.caseHighlights.map((c, i) => (
                    <div key={i} className="attorney-case-card">
                      <div className="case-number">0{i + 1}</div>
                      <div>
                        <h4>{c.title}</h4>
                        <p>{c.outcome}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="attorney-modal-footer">
              <a href="#contact" className="button button-gold" onClick={closeModal}>
                Schedule a Consultation
              </a>
            </div>
          </div>
        </div>
      )}
    </>
  );
}